// Logica pura di Statistics (periodi, date, formati): testabile senza React.

export const PERIODS = ["today", "tomorrow", "yesterday", "last7", "last30", "thisMonth", "lastMonth", "custom"];
export const DEFAULT_PERIOD = "last7";

const pad = (n) => String(n).padStart(2, "0");
// Data locale -> AAAA-MM-GG (senza passare da UTC)
export const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseIso = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d, 12);
};
export const addDays = (iso, n) => {
  const d = parseIso(iso);
  d.setDate(d.getDate() + n);
  return isoOf(d);
};
// Giorni compresi tra due date, estremi inclusi
export const daysCount = (from, to) => Math.round((parseIso(to) - parseIso(from)) / 86400000) + 1;

// Intervallo {from, to} di un periodo; `today` è AAAA-MM-GG. Personalizzato: se Dal > al si scambiano.
export const periodRange = (key, today, custom = {}) => {
  const t = parseIso(today);
  switch (key) {
    case "today":
      return { from: today, to: today };
    case "tomorrow": {
      const d = addDays(today, 1);
      return { from: d, to: d };
    }
    case "yesterday": {
      const d = addDays(today, -1);
      return { from: d, to: d };
    }
    case "last30":
      return { from: addDays(today, -29), to: today };
    case "thisMonth":
      return { from: isoOf(new Date(t.getFullYear(), t.getMonth(), 1, 12)), to: today };
    case "lastMonth":
      return {
        from: isoOf(new Date(t.getFullYear(), t.getMonth() - 1, 1, 12)),
        to: isoOf(new Date(t.getFullYear(), t.getMonth(), 0, 12)),
      };
    case "custom": {
      const from = custom.from || today;
      const to = custom.to || from;
      return from > to ? { from: to, to: from } : { from, to };
    }
    case "last7":
    default:
      return { from: addDays(today, -6), to: today };
  }
};

const localeOf = (lang) => (lang === "en" ? "en-GB" : "it-IT");

// "26 ottobre 2026"
export const longDate = (iso, lang = "it") =>
  parseIso(iso).toLocaleDateString(localeOf(lang), { day: "numeric", month: "long", year: "numeric" });

// "lun 26/10"
export const shortDay = (iso, lang = "it") => {
  const d = parseIso(iso);
  const wd = d.toLocaleDateString(localeOf(lang), { weekday: "short" }).replace(".", "");
  return `${wd} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
};

// Euro: "€ 42", "€ 1.234,50" (it) / "€42", "€1,234.50" (en); null -> "—"
export const euro = (n, lang = "it") => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  const [th, dec] = lang === "en" ? [",", "."] : [".", ","];
  const abs = Math.round(Math.abs(v) * 100);
  const int = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, th);
  const cents = abs % 100 ? `${dec}${pad(abs % 100)}` : "";
  const text = `${v < 0 ? "-" : ""}${int}${cents}`;
  return lang === "en" ? `€${text}` : `€ ${text}`;
};

// Percentuale intera di part su total; 0 se total è 0
export const pct = (part, total) => (total ? Math.round((part / total) * 100) : 0);

// Giorni del grafico: dal più vecchio al più recente (l'API li dà dal più recente)
export const chartDays = (days = []) => [...days].sort((a, b) => (a.date < b.date ? -1 : 1));

// Totali della tabella Dettaglio per giorno
export const dayTotals = (days = []) =>
  days.reduce(
    (acc, r) => ({
      bookings: acc.bookings + (r.bookings || 0),
      lunch: acc.lunch + (r.lunch || 0),
      dinner: acc.dinner + (r.dinner || 0),
      total: acc.total + (r.total || 0),
      no_show: acc.no_show + (r.no_show || 0),
      revenue: r.revenue === null || r.revenue === undefined ? acc.revenue : (acc.revenue || 0) + Number(r.revenue),
    }),
    { bookings: 0, lunch: 0, dinner: 0, total: 0, no_show: 0, revenue: null }
  );

// Provenienze presenti, dalla più numerosa
export const sourceRows = (sources = {}) =>
  Object.entries(sources)
    .filter(([, n]) => n > 0)
    .map(([code, n]) => ({ code, n }))
    .sort((a, b) => b.n - a.n);
